import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import './premium-gate.css';

function authHeaders() {
  const token = localStorage.getItem('adeeb-token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Global subscription-gate modal. Mounted once at the App root and
 * shown whenever any component calls `openGate()` from
 * SubscriptionContext. Designed to overlay the current page (so the
 * user keeps their place in the library).
 */
export default function PremiumGateModal() {
  const { gateOpen, gateReturnPath, closeGate } = useSubscription();
  const navigate = useNavigate();
  const signedIn = Boolean(localStorage.getItem('adeeb-token'));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!gateOpen) return;
    setBusy(false);
    setError('');
    const onKey = (e) => { if (e.key === 'Escape') closeGate(); };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while the modal is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [gateOpen, closeGate]);

  if (!gateOpen) return null;

  const handleSubscribe = async () => {
    if (busy) return;
    if (!signedIn) {
      // Checkout needs an account: create one first, then land on the subscribe page.
      const next = gateReturnPath || '/profile';
      closeGate();
      navigate(`/register?redirect=${encodeURIComponent('/subscribe?next=' + encodeURIComponent(next))}`);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post(
        '/api/billing/checkout',
        { next: gateReturnPath || '/profile' },
        { headers: authHeaders() }
      );
      window.location.href = data.url;
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not start checkout. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div
      className="premium-gate-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="premium-gate-title"
      onClick={(e) => { if (e.target === e.currentTarget) closeGate(); }}
    >
      <div className="premium-gate-card">
        <button
          type="button"
          className="premium-gate-close"
          aria-label="Close"
          onClick={closeGate}
        >
          ×
        </button>

        <div className="premium-gate-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </svg>
        </div>

        <div className="premium-gate-eyebrow">Deenlit Premium</div>
        <h2 id="premium-gate-title" className="premium-gate-title">
          Unlock the full library
        </h2>
        <p className="premium-gate-sub">
          Subscribe to read every book, take quizzes and listen to audio narration.
        </p>

        <ul className="premium-gate-features">
          <li><span className="premium-gate-check">✓</span> Full library access</li>
          <li><span className="premium-gate-check">✓</span> Audio narration on every book</li>
          <li><span className="premium-gate-check">✓</span> Quizzes, achievements &amp; rewards</li>
          <li><span className="premium-gate-check">✓</span> Cancel anytime</li>
        </ul>

        <div className="premium-gate-price">
          <span className="premium-gate-price-amount">£7.50</span>
          <span className="premium-gate-price-period">/ month</span>
        </div>

        <button
          type="button"
          className="premium-gate-cta"
          onClick={handleSubscribe}
          disabled={busy}
        >
          {busy ? 'Redirecting to Stripe…' : signedIn ? 'Subscribe now' : 'Create account to subscribe'}
        </button>

        {error && <div className="premium-gate-error">{error}</div>}

        <button type="button" className="premium-gate-dismiss" onClick={closeGate} disabled={busy}>
          Maybe later
        </button>

        <div className="premium-gate-fineprint">
          Secured by Stripe · We never store your card details
        </div>
      </div>
    </div>
  );
}
